/**
 * AI Visibility Impact Detail
 * 
 * Full detail view (modal/drawer) showing:
 * - Detailed score breakdown
 * - Contributing factors with explanations
 * - Mutual growth partners
 * - Human-readable explanation
 */

import React from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { InsightIcon, CommunityIcon, WellnessIcon, ExpertIcon, CompanyIcon, PartnerIcon } from '../icons';
import { AIVisibilityImpactData, FactorStatus } from '../../types/aiVisibilityImpact.types';

interface AIVisibilityImpactDetailProps {
  userId: string;
  data: AIVisibilityImpactData;
  onClose: () => void;
  onRefresh: () => void;
}

const AIVisibilityImpactDetail: React.FC<AIVisibilityImpactDetailProps> = ({
  data,
  onClose,
  onRefresh
}) => {
  const { score, factors, mutualGrowth, explanation } = data;
  const { score: currentScore, trend } = score;

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-gray-400';

  const scoreColor = currentScore >= 80 ? 'text-emerald-600' : 
                     currentScore >= 60 ? 'text-blue-600' : 
                     currentScore >= 40 ? 'text-yellow-600' : 'text-gray-600';

  const getStatusColor = (status: FactorStatus) => {
    switch (status) {
      case 'groeiend':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'stabiel':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'laag':
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <InsightIcon size={24} weight="regular" className="text-[#5F6B3A]" />
              <div>
                <h2 className="text-xl font-medium text-gray-700">Visibility Impact</h2>
                <p className="text-sm text-gray-500">LLM-vindbaarheid en AI-organic impact</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Score Section */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">LLM Visibility Score</h3>
                  <p className="text-sm text-gray-600">
                    Hoe goed AI-zoekmachines jouw expertise kunnen begrijpen en aanbevelen
                  </p>
                </div>
              </div>
              <div className="flex items-baseline space-x-3 mb-4">
                <span className={`text-5xl font-bold ${scoreColor}`}>{currentScore}</span>
                <span className="text-xl text-gray-500">/ 100</span>
                {trend !== 'stable' && (
                  <div className="flex items-center space-x-1">
                    <TrendIcon size={24} className={trendColor} />
                    <span className={`text-sm font-medium ${trendColor}`}>
                      {trend === 'up' ? 'Groeiend' : 'Dalend'}
                    </span>
                  </div>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    currentScore >= 80 ? 'bg-emerald-500' :
                    currentScore >= 60 ? 'bg-blue-500' :
                    currentScore >= 40 ? 'bg-yellow-500' : 'bg-gray-500'
                  }`}
                  style={{ width: `${currentScore}%` }}
                ></div>
              </div>
            </div>

            {/* Contributing Factors */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Wat draagt bij aan jouw zichtbaarheid
              </h3>
              <div className="space-y-4">
                {/* Connections */}
                <FactorCard
                  icon={CommunityIcon}
                  title="Connecties"
                  factor={factors.connections}
                  description="Een connectie telt alleen mee als de connectieflow volledig is doorlopen, relevant is (expertise/sector/regio overlap), en actief is (interactie in laatste 30-60 dagen). Geen mass connecten, geen slapende relaties."
                />

                {/* Mutual Respect */}
                <FactorCard
                  icon={WellnessIcon}
                  title="Mutual Respect"
                  factor={factors.mutualRespect}
                  description="Consistente respectscore, respectvolle interacties, en geen negatieve content (CleanMind filter). Dit is het fundament voor betrouwbaarheid en essentieel voor AI-aanbevelingen."
                />

                {/* Expert Status */}
                <FactorCard
                  icon={ExpertIcon}
                  title="Expert Status"
                  factor={factors.expertStatus}
                  description="Duidelijke expertise topics, consistente positionering, en gedrag dat past bij expertise. Dit maakt je een herkenbare AI-entiteit."
                />

                {/* Network Clubs */}
                <FactorCard
                  icon={CompanyIcon}
                  title="Netwerkclubs"
                  factor={factors.networkClubs}
                  description="Maximaal 2 actieve netwerkclubs tegelijk. Moet inhoudelijk of regionaal relevant zijn en actieve deelname vereist. Contextuele versterking en regionale/thematische clustering."
                />

                {/* Collaborations */}
                <FactorCard
                  icon={PartnerIcon}
                  title="Samenwerkingen"
                  factor={factors.collaborations}
                  description="Getagd worden door anderen, co-connecties, en gezamenlijke clubactiviteit. Wederkerigheid is belangrijk - geen spam tagging. Vermenigvuldigt context en bereik."
                />
              </div>
            </div>

            {/* Mutual Growth */}
            {mutualGrowth.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Mutual Growth
                </h3>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Mensen met wie je samen groeit. Geen rankings, focus op netwerk-effect.
                  </p>
                  <div className="space-y-2">
                    {mutualGrowth.map((partner) => (
                      <div key={partner.userId} className="flex items-center space-x-3 p-2 bg-white rounded-lg">
                        {partner.userPhoto && (
                          <img
                            src={partner.userPhoto}
                            alt={partner.userName}
                            className="w-10 h-10 rounded-full"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-sm text-gray-900">{partner.userName}</p>
                          <p className="text-xs text-gray-500">
                            {partner.sharedConnections} gedeelde connecties, {partner.sharedClubs} gedeelde clubs
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Explanation */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Uitleg in mensentaal</h4>
              <p className="text-sm text-gray-700 mb-3">{explanation}</p>
              <div className="text-xs text-gray-600 space-y-1">
                <p>• LLM's werken met entiteiten en relaties</p>
                <p>• Context is belangrijker dan volume</p>
                <p>• Dit systeem is niet te manipuleren</p>
                <p>• Geen likes, geen pay-to-win, geen verborgen algoritmes</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface FactorCardProps {
  icon: React.ComponentType<any>;
  title: string;
  factor: {
    status: FactorStatus;
    description: string;
    [key: string]: any;
  };
  description: string;
}

const FactorCard: React.FC<FactorCardProps> = ({ icon: Icon, title, factor, description }) => {
  const getStatusColor = (status: FactorStatus) => {
    switch (status) {
      case 'groeiend':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'stabiel':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'laag':
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start space-x-3 mb-3">
        <div className="flex-shrink-0 pt-0.5">
          <Icon size={22} weight="regular" className="text-[#5F6B3A]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-semibold text-gray-900">{title}</h4>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(factor.status)}`}>
              {factor.status}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">{factor.description}</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
    </div>
  );
};

export default AIVisibilityImpactDetail;
